import type { CategoryData } from '../types'

// API 基础 URL，从环境变量读取
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api"

// JSON:API 资源类型定义（对应新接口响应格式）
interface JsonApiLinkResource {
  type: 'link'
  id: string
  attributes: {
    title: string
    desc: string
    url: string
    categoryId: string
  }
}

interface JsonApiCategoryResource {
  type: 'category'
  id: string
  attributes: {
    name: string
    links: JsonApiLinkResource[]
  }
}

// JSON:API 文档（列表响应）
interface JsonApiListDocument {
  jsonapi: { version: string }
  data: JsonApiCategoryResource[] | null
  meta?: Record<string, unknown>
  errors?: Array<{
    status: string
    code: string
    title: string
    detail: string
    meta?: Record<string, unknown>
  }>
}

/**
 * 将 JSON:API 响应中的分类资源数组转换为应用内部使用的 CategoryData 结构
 */
function transformToCategoryData(resources: JsonApiCategoryResource[]): CategoryData[] {
  return resources.map((resource) => ({
    category: resource.attributes.name,
    children: resource.attributes.links.map((link) => ({
      title: link.attributes.title,
      desc: link.attributes.desc,
      url: link.attributes.url,
    })),
  }))
}


// 使用完整的外部 URL，开发和生产环境都直接访问
const STORAGE_KEY = 'magic-nav-data-cache'
const STORAGE_TIMESTAMP_KEY = 'magic-nav-data-timestamp'

/**
 * 从 LocalStorage 读取缓存的数据
 */
export function getCachedData(): CategoryData[] | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
    return null
  } catch (error) {
    console.error('Failed to read from localStorage:', error)
    return null
  }
}

/**
 * 保存数据到 LocalStorage
 */
export function saveToCache(data: CategoryData[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString())
  } catch (error) {
    console.error('Failed to save to localStorage:', error)
  }
}

/**
 * 检查数据是否发生变化
 */
export function hasDataChanged(oldData: CategoryData[], newData: CategoryData[]): boolean {
  const oldStr = JSON.stringify(oldData)
  const newStr = JSON.stringify(newData)
  return oldStr !== newStr
}

/**
 * 获取缓存的时间戳
 */
export function getCacheTimestamp(): number | null {
  try {
    const timestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY)
    return timestamp ? parseInt(timestamp, 10) : null
  } catch (error) {
    console.error('Failed to read timestamp from localStorage:', error)
    return null
  }
}

/**
 * 清除缓存
 */
export function clearCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY)
  } catch (error) {
    console.error('Failed to clear cache:', error)
  }
}

/**
 * 从 API 获取工具数据
 */
export async function fetchToolsData(): Promise<CategoryData[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/link`, {
      headers: {
        Accept: 'application/vnd.api+json',
        'User-Agent': 'Mozilla/5.0 (compatible; MagicNav/1.0)',
      },
    })

    // 以 HTTP 状态码判定成功/失败（2xx 成功，4xx/5xx 失败）
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const document: JsonApiListDocument = await response.json()

    // data 为分类资源数组，转换为本地使用的 CategoryData 结构
    const resources = Array.isArray(document.data) ? document.data : []
    return transformToCategoryData(resources)
  } catch (error) {
    console.error('Failed to fetch tools data:', error)
    throw error
  }
}
